import { useEffect, useState } from 'react';
import { api } from '../../services/api';
import type { AdminPackageRow } from '../../types/packages';
import { formatDay } from '../offers/offerReviewModel';
import PackageStatusChip from './PackageStatusChip';
import { approvalState, modeLabel, priceText, unavailableReasonLabel } from './packageReviewModel';

export interface GaragePackagesTabProps {
  garageId: string;
  token: string;
}

/**
 * Spec 081b — the Packages tab of the admin garage page: every package of this garage, any status.
 * Rows open the review page in a new tab so the garage page keeps its place. Plain anchors (not
 * router links) because a new tab is a fresh page load anyway.
 */
export default function GaragePackagesTab(props: GaragePackagesTabProps) {
  const { garageId, token } = props;
  const [rows, setRows] = useState<AdminPackageRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setRows(null);
    setError(null);
    api.listGaragePackages(garageId, token)
      .then((list) => { if (!cancelled) setRows(Array.isArray(list) ? list : []); })
      .catch((err) => { if (!cancelled) setError(err instanceof Error ? err.message : 'Could not load packages'); });
    return () => { cancelled = true; };
  }, [garageId, token]);

  if (error) {
    return <div className="admin-info-banner danger" role="alert">{error}</div>;
  }

  return (
    <div className="admin-card" data-testid="garage-packages-tab">
      <div className="admin-card-header">
        <h3 className="admin-card-title">Packages{rows && rows.length > 0 ? ` (${rows.length})` : ''}</h3>
      </div>
      {rows == null ? (
        <div className="admin-loading"><div className="admin-loading-spinner"></div></div>
      ) : rows.length === 0 ? (
        <div className="admin-empty-state"><p>This garage has no packages yet.</p></div>
      ) : (
        <div className="admin-table-wrapper">
          <table className="admin-table package-garage-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Status</th>
                <th>Pricing</th>
                <th>Starts</th>
                <th>Ends</th>
                <th>Approval</th>
                <th>Unavailable</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id} data-testid="garage-package-row">
                  <td>
                    <a href={`/service-approvals/packages/${row.id}`} target="_blank" rel="noopener noreferrer" className="admin-link"
                      style={{ fontWeight: 500 }}>
                      {row.nameEn}
                    </a>
                    <div className="admin-text-muted">{row.itemCount} services</div>
                  </td>
                  <td><PackageStatusChip status={row.status} /></td>
                  <td>
                    <div>{modeLabel(row)}</div>
                    <strong>{priceText(row)}</strong>
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}>{row.startsAt ? formatDay(row.startsAt) : 'On approval'}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>{row.endsAt ? formatDay(row.endsAt) : 'No end date'}</td>
                  <td>{approvalState(row)}</td>
                  <td>
                    {row.unavailableReason
                      ? <span className="admin-badge warning">{unavailableReasonLabel(row.unavailableReason)}</span>
                      : <span className="admin-text-muted">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
